import React, { useEffect } from 'react';
import { BaseFacet } from './BaseFacet';
import { useCollapsibleActions } from '../CollapsibleSection';
import { COUNTRY_MAPPING } from '../../utils/constants';

interface CustomCountryFacetProps {
  show?: number;
}

// Inner component that registers actions with CollapsibleSection context
const CustomCountryFacetInner: React.FC<{
  options: any[];
  selectedValues: Set<string>;
  handleToggle: (value: string) => void;
  clearAll: () => void;
  selectAll: () => void;
  hasFilters: boolean;
  label: string;
}> = ({ options, selectedValues, handleToggle, clearAll, selectAll, hasFilters, label }) => {
  const collapsibleActions = useCollapsibleActions();

  useEffect(() => {
    if (collapsibleActions) {
      collapsibleActions.registerActions({
        selectAll,
        clearAll,
        optionCount: options.length,
      });
    }
  }, [options, selectedValues, collapsibleActions]);

  return (
    <div className="custom-facet-container">
      <div className="custom-facet-options">
        {options.map((option: any) => (
          <label
            key={option.originalValue}
            className={`custom-facet-option ${selectedValues.has(option.originalValue) ? 'selected' : ''}`}
          >
            <input
              type="checkbox"
              checked={selectedValues.has(option.originalValue)}
              onChange={() => handleToggle(option.originalValue)}
              className="custom-facet-checkbox"
            />
            <span className="flex-1">{option.displayName}</span>
            <span className="custom-facet-count">({option.count})</span>
          </label>
        ))}
      </div>
    </div>
  );
};

export const CustomCountryFacet: React.FC<CustomCountryFacetProps> = ({
  show = 30,
}) => {
  const transformOptions = (options: any[]) => {
    // Map country codes to full names
    return options
      .map((option) => {
        const countryCode = option.value;
        const countryInfo = COUNTRY_MAPPING[countryCode];

        if (countryInfo) {
          return {
            ...option,
            displayName: ` ${countryInfo.name}`,
            originalValue: countryCode, // Keep original for filtering
          };
        } else {
          // For unknown country codes, show as-is
          return {
            ...option,
            displayName: option.value,
            originalValue: countryCode,
          };
        }
      })
      .sort((a, b) => {
        const aIsOther =
          a.displayName.toLowerCase().includes('other') ||
          a.displayName.toLowerCase().includes('not specified');
        const bIsOther =
          b.displayName.toLowerCase().includes('other') ||
          b.displayName.toLowerCase().includes('not specified');

        // Both are "other" type - sort by count descending
        if (aIsOther && bIsOther) {
          return b.count - a.count;
        }

        // Only a is "other" - a should go to bottom
        if (aIsOther && !bIsOther) return 1;

        // Only b is "other" - b should go to bottom
        if (!aIsOther && bIsOther) return -1;

        // Neither are "other" - sort by count descending
        return b.count - a.count;
      });
  };

  return (
    <BaseFacet
      field="listing_country_code_txt"
      label="Country"
      show={show}
      sortType="count"
      otherAtBottom={true}
      transformOptions={transformOptions}
    >
      {({
        options,
        selectedValues,
        handleToggle,
        clearAll,
        selectAll,
        hasFilters,
        label,
      }: any) => (
        <CustomCountryFacetInner
          options={options}
          selectedValues={selectedValues}
          handleToggle={handleToggle}
          clearAll={clearAll}
          selectAll={selectAll}
          hasFilters={hasFilters}
          label={label}
        />
      )}
    </BaseFacet>
  );
};
